import { ComponentStyleConfig } from '@chakra-ui/react';
import { z } from 'zod';

import { SystemStyleObject } from '@chakra-ui/theme-tools';
import { clone, merge } from 'lodash';
import { textStyles } from '../../common';

const propsSchema = z.object({
  theme: z.record(z.any()),
  colorScheme: z
    .enum(['green', 'gray', 'red', 'purple', 'yellow', 'text'])
    .catch('green'),
});

const withKnownProps =
  <T>(f: (props: z.output<typeof propsSchema>) => T) =>
  (props: unknown) =>
    f(propsSchema.parse(props));

const solid = ({ colorScheme }: z.output<typeof propsSchema>) =>
  withDisabled({
    background: `button.${colorScheme}`,
    color: `button.${colorScheme}.text`,
    borderColor: 'whiteAlpha.300',
    _hover: hoverFill(colorScheme),
    _active: activeFill(colorScheme),
  });

const fill =
    (state: 'hover' | 'active') =>
    (colorScheme: z.output<typeof propsSchema>['colorScheme']) =>
      ({
        background: `button.${colorScheme}.${state}`,
        borderColor: 'whiteAlpha.300',
        color: `button.${colorScheme}.text`,
      }) satisfies SystemStyleObject,
  hoverFill = fill('hover'),
  activeFill = fill('active');

const withDisabled = (styles: SystemStyleObject): SystemStyleObject => {
  const disabled = {};
  const result = Object.assign(clone(styles), {
    _disabled: { '&&': disabled },
  });
  for (const prop of ['color', 'background', 'borderColor'] as const) {
    if (result[prop]) {
      Object.assign(disabled, { [prop]: result[prop] });
    }
  }
  return result;
};

export const ButtonStyles: ComponentStyleConfig = {
  baseStyle: {
    borderRadius: '8px',
    outlineWidth: '2px',
    outlineOffset: '1px',
    border: 'solid 1px transparent',
    '&&': {
      boxShadow: 'none',
    },
    _focusVisible: {
      outlineColor: 'state.selected',
    },
    _disabled: {
      opacity: 0.5,
    },
  } satisfies SystemStyleObject,
  sizes: {
    lg: {
      ...textStyles['body-md'],
      fontWeight: '500',
      py: 2.5,
      px: 4,
      h: 10,
    },
    md: {
      ...textStyles['body-md'],
      fontWeight: '500',
      h: 8,
      py: 2,
      px: 2,
    },
    sm: {
      ...textStyles['body-sm'],
      fontWeight: '500',
      h: 7,
      py: 1.5,
      px: 2,
    },
    xs: {
      ...textStyles['body-sm'],
      fontWeight: '500',
      h: 6,
      py: 1,
      px: 2,
    },
    'box-xs': {
      w: 6,
      h: 6,
      p: '5px',
      fontSize: '12px',
    },
    'box-sm': {
      w: 7,
      h: 7,
      p: '7px',
      fontSize: '12px',
    },
    'box-md': {
      w: 8,
      h: 8,
      p: 2,
      fontSize: '14px',
    },
    'box-lg': {
      w: 10,
      h: 10,
      p: 3,
      fontSize: '14px',
    },
  },
  variants: {
    solid: withKnownProps(solid),
    outline: withKnownProps(({ colorScheme }) =>
      withDisabled({
        color: `button.${colorScheme}.text-outline`,
        borderColor: `button.${colorScheme}.outline`,
        _hover: hoverFill(colorScheme),
        _active: activeFill(colorScheme),
      }),
    ),
    ghost: withKnownProps(({ colorScheme }) =>
      withDisabled({
        color: `button.${colorScheme}.${colorScheme === 'green' ? '' : `text-`}outline`,
        borderColor: 'transparent',
        _hover: hoverFill(colorScheme),
        _active: activeFill(colorScheme),
      }),
    ),
    link: withKnownProps(
      ({ colorScheme }) =>
        ({
          color: `button.${colorScheme}.${colorScheme === 'green' ? '' : 'text-'}outline`,
        }) satisfies SystemStyleObject,
    ),
    pill: withKnownProps((props) =>
      merge(solid(props), {
        borderRadius: '999px',
      }),
    ),
    dataTablePanelSelectedAction: withKnownProps(solid),
    unstyled: {
      display: 'flex',
    },
  },
  defaultProps: {
    variant: 'unstyled',
    colorScheme: 'green',
  },
};
